import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, History, Loader2 } from "lucide-react";
import { fetchLawyerPricingHistory } from "@lawyer/services/lawyerApi";

const rupees = (paise) => (paise == null ? null : `₹${Math.round(paise / 100).toLocaleString("en-IN")}`);
const when = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const sourceLabel = (e) => (e.source === "application_approve"
  ? `Your request${e.requested_level && e.requested_level !== e.level ? ` (you asked for L${e.requested_level})` : ""}`
  : "Set by Advoset Admin");

// Lawyer-facing level timeline (same facts the admin sees, minus admin identities).
// `initialState`: a page-level prefetched result ({ entries } or null on failure) — skips the mount-time fetch.
export default function LevelHistoryCard({ refreshKey, initialState }) {
  const [state, setState] = useState(initialState !== undefined
    ? { loading: false, entries: initialState?.entries || [], error: initialState === null }
    : { loading: true, entries: [], error: false });
  const mountKey = useRef(refreshKey);
  useEffect(() => {
    if (initialState !== undefined && refreshKey === mountKey.current) return;
    setState((s) => ({ ...s, loading: true }));
    fetchLawyerPricingHistory()
      .then((d) => setState({ loading: false, entries: d.entries || [], error: false }))
      .catch(() => setState({ loading: false, entries: [], error: true }));
  }, [refreshKey, initialState]);

  const latest = state.entries[0];
  return (
    <details className="group bg-white rounded-2xl border border-slate-200 p-5 max-w-3xl" data-testid="lawyer-level-history">
      <summary className="flex items-center gap-3 cursor-pointer list-none [&::-webkit-details-marker]:hidden" data-testid="lawyer-level-history-toggle">
        <span className="h-8 w-8 rounded-lg bg-slate-100 text-[#0A2540] grid place-items-center shrink-0"><History size={14} /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold text-[#0A2540]">Level history</span>
          {latest && !state.loading && (
            <span className="block text-xs text-slate-500 truncate" data-testid="lawyer-level-history-latest">
              {when(latest.at)} · Level {latest.level ?? "?"}{rupees(latest.base_fee_paise) ? ` · ${rupees(latest.base_fee_paise)}` : ""} · {sourceLabel(latest)}
            </span>
          )}
        </span>
        {state.loading ? <Loader2 size={14} className="animate-spin text-slate-400" /> : <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180 shrink-0" />}
      </summary>
      {state.error && <p className="mt-3 text-sm text-rose-600" data-testid="lawyer-level-history-error">Could not load your level history.</p>}
      {!state.loading && !state.error && state.entries.length === 0 && (
        <p className="mt-3 text-sm text-slate-500" data-testid="lawyer-level-history-empty">No level has been set on your account yet.</p>
      )}
      {state.entries.length > 0 && (
        <ol className="mt-3 divide-y divide-slate-100 max-h-64 overflow-y-auto" data-testid="lawyer-level-history-list">
          {state.entries.map((e, i) => (
            <li key={`${e.at}-${i}`} className="py-2 text-xs" data-testid={`lawyer-level-history-entry-${i}`}>
              <div className="flex flex-wrap items-baseline gap-x-1.5 text-slate-600">
                <span data-testid={`lawyer-level-history-when-${i}`}>{when(e.at)}</span>
                <span>·</span>
                <span className="font-extrabold text-[#0A2540]" data-testid={`lawyer-level-history-level-${i}`}>
                  {e.previous_level != null && e.previous_level !== e.level ? `Level ${e.previous_level} → ` : ""}Level {e.level ?? "?"}
                </span>
                {rupees(e.base_fee_paise) && <><span>·</span><span>{rupees(e.base_fee_paise)}</span></>}
                <span>·</span>
                <span className="text-slate-500">{sourceLabel(e)}</span>
              </div>
              {e.reason && <p className="text-slate-700 mt-0.5" data-testid={`lawyer-level-history-why-${i}`}>“{e.reason}”</p>}
            </li>
          ))}
        </ol>
      )}
    </details>
  );
}
